"use client";

import { useEffect, type RefObject } from "react";
import { reportDockRect } from "@/lib/dock-window";

/** The island: a popover portaled to <body>, so never under the dock root. */
const ISLAND_SELECTOR = ".cv2-island";

/** What the window host's rect covers (spec 2026-09-22 §4): the ball's stage,
 *  or the box's card and its thread, whichever is mounted, plus the island
 *  and the crown while either is up (both open above the ball), and the row
 *  under the ball and the Line's tray, which overflow the 84 px stage. The
 *  tray sits in that row and stays mounted while it sinks, so it counts only
 *  while up. What the shell's parity parts draw (the task dots, a moment)
 *  marks itself data-dock-hit. */
const DOCK_RECT_SELECTOR =
  ".cl-stage, .cl-stage__under > :not(.cl-tray), .cl-tray[data-up], [data-testid='bh-pill'], .bh-thread-wrap, .cl-crown[data-up='1'], [data-dock-hit]";

/**
 * The window host's click rect (spec 2026-09-22 §4), reported to the shell.
 * Gated on host === "window": the page host runs none of it.
 */
export function useDockWindowRect(
  host: "page" | "window",
  dockRef: RefObject<HTMLDivElement | null>,
  ballUp: boolean
) {
  // The shell grants clicks only inside the rect the page reports: the
  //    transparent margin of the fixed panel must stay click-through. The
  //    root spans the panel's full width, so the rect is the union of what is
  //    on screen to click instead: the ball's stage while the ball is up, the
  //    box's card and its thread while the door is open, and the island and
  //    the crown while either is up. Each is observed on its own (the card
  //    grows as the thread opens), and the set is taken again when the door
  //    changes, since the ball and the box swap elements, and when the island
  //    or the crown goes up or down: the island flips a class and the crown
  //    mounts with its paper, neither of which a size observer sees.
  useEffect(() => {
    if (host !== "window") return;
    const root = dockRef.current;
    if (!root) return;
    // The island is a popover portaled to <body>, outside the root, so it is
    // looked up on the document; everything else lives under the root.
    const targets = () => [
      ...root.querySelectorAll<HTMLElement>(DOCK_RECT_SELECTOR),
      ...document.querySelectorAll<HTMLElement>(ISLAND_SELECTOR),
    ];
    const ro = new ResizeObserver(() => schedule());
    const observe = () => {
      ro.disconnect();
      for (const el of targets()) ro.observe(el);
    };
    // A mutation only marks the set stale; the next frame's report takes it
    // again, so a streaming thread re-queries once a frame, not per node.
    let stale = false;
    let sent: { x: number; y: number; w: number; h: number } | null = null;
    let raf = 0;
    const report = () => {
      raf = 0;
      if (stale) {
        stale = false;
        observe();
      }
      let l = Infinity;
      let t = Infinity;
      let r = -Infinity;
      let b = -Infinity;
      for (const el of targets()) {
        const box = el.getBoundingClientRect();
        if (box.width === 0 || box.height === 0) continue;
        l = Math.min(l, box.left);
        t = Math.min(t, box.top);
        r = Math.max(r, box.right);
        b = Math.max(b, box.bottom);
      }
      // Nothing on screen to click is an empty rect, never the last one: a
      // stale rect would keep a band of the desktop from its clicks. The same
      // rect twice is not sent twice (a streaming thread mutates every frame).
      const next =
        l === Infinity
          ? { x: 0, y: 0, w: 0, h: 0 }
          : { x: l, y: t, w: r - l, h: b - t };
      if (
        sent &&
        sent.x === next.x &&
        sent.y === next.y &&
        sent.w === next.w &&
        sent.h === next.h
      )
        return;
      sent = next;
      reportDockRect(next);
    };
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(report);
    };
    observe();
    report();
    const mo = new MutationObserver(() => {
      stale = true;
      schedule();
    });
    mo.observe(root, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["class", "data-up"],
    });
    // The island mounts and unmounts as a child of <body> (the portal), which
    // the root's observer above cannot see.
    mo.observe(document.body, { childList: true });
    // The crown opens from a scale over 0.64 s, and a transform is invisible
    // to the size observer: the end of any transition or animation under the
    // root (both bubble) re-reads the rect, closing ones included.
    root.addEventListener("transitionend", schedule);
    root.addEventListener("animationend", schedule);
    // The island's open and close are animations outside the root: its end
    // re-reads the rect the same way.
    const onIslandAnimationEnd = (e: Event) => {
      if ((e.target as Element).closest?.(ISLAND_SELECTOR)) schedule();
    };
    document.addEventListener("animationend", onIslandAnimationEnd);
    window.addEventListener("resize", schedule);
    return () => {
      document.removeEventListener("animationend", onIslandAnimationEnd);
      root.removeEventListener("transitionend", schedule);
      root.removeEventListener("animationend", schedule);
      mo.disconnect();
      ro.disconnect();
      window.removeEventListener("resize", schedule);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [host, dockRef, ballUp]);
}
